import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCCQInfo } from '@/lib/ccq-holidays';

export function MiniCalendar({ month, onMonthChange, selectedDates, onToggleDate, equipeOccupee, congesEquipe }) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const joursSemaine = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  const firstDay = new Date(year, m, 1);
  const lastDay = new Date(year, m + 1, 0);
  const startDow = firstDay.getDay();
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const prevMonth = () => onMonthChange(new Date(year, m - 1, 1));
  const nextMonth = () => onMonthChange(new Date(year, m + 1, 1));
  const toDateStr = (day) => `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const isSelected = (day) => day && selectedDates.includes(toDateStr(day));
  const isLeftEdge = (day) => { if (!isSelected(day)) return false; const prev = new Date(year, m, day - 1); return !selectedDates.includes(`${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,'0')}-${String(prev.getDate()).padStart(2,'0')}`); };
  const isRightEdge = (day) => { if (!isSelected(day)) return false; const next = new Date(year, m, day + 1); return !selectedDates.includes(`${next.getFullYear()}-${String(next.getMonth()+1).padStart(2,'0')}-${String(next.getDate()).padStart(2,'0')}`); };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="w-5 h-5" /></button>
        <span className="font-semibold">{monthNames[m]} {year}</span>
        <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0">
        {joursSemaine.map((j, i) => (<div key={`${j}-${i}`} className="text-center text-xs font-medium text-gray-500 py-1">{j}</div>))}
        {days.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;
          const dateStr = toDateStr(day);
          const sel = isSelected(day);
          const dayOfWeek = new Date(year, m, day).getDay();
          const weekend = dayOfWeek === 0 || dayOfWeek === 6;
          const ccq = getCCQInfo(dateStr);
          const isFerie = !!(ccq?.jourFerie || ccq?.conge);
          const nomFerie = ccq?.jourFerie?.nom || ccq?.conge?.nom || '';
          const occupe = equipeOccupee?.[dateStr];
          const conge = congesEquipe?.[dateStr];
          // 🆕 [v9.3 — 2026-06-12] Plus aucun blocage par défaut : weekend ET
          // jour férié restent visuellement distincts mais CLIQUABLES (demande
          // explicite d'Eliot : "fais comme les fins de semaine, ne force pas
          // le BT dedans pour rien"). Aligne ce MiniCalendar sur
          // EditerBonTravailDialog v9.3.
          const blocked = false;
          const leftE = isLeftEdge(day);
          const rightE = isRightEdge(day);

          let cls = 'py-2 text-sm font-medium transition-colors relative ';
          if (sel) {
            // Sélectionné : couleur de fond selon contexte
            if (isFerie) cls += 'bg-rose-600 text-white ring-2 ring-rose-300 ';
            else if (weekend) cls += 'bg-amber-500 text-white ring-2 ring-amber-300 ';
            else cls += 'bg-blue-500 text-white ';
          } else if (isFerie) {
            cls += 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-dashed border-rose-300 ';
          } else if (conge) {
            cls += 'bg-amber-100 text-amber-600 ';
          } else if (occupe) {
            cls += 'bg-orange-50 text-orange-600 border-b-2 border-orange-400 ';
          } else if (weekend) {
            cls += 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-dashed border-amber-300 ';
          } else {
            cls += 'hover:bg-blue-50 text-gray-700 ';
          }
          if (leftE) cls += 'rounded-l-lg ';
          if (rightE) cls += 'rounded-r-lg ';

          return (
            <button
              key={day}
              type="button"
              onClick={() => !blocked && onToggleDate(dateStr)}
              disabled={blocked}
              title={
                isFerie ? `${nomFerie || 'Congé CCQ'} — cliquez pour céduler quand même`
                : weekend ? 'Weekend — cliquez pour céduler quand même'
                : conge ? `Congé: ${conge}`
                : occupe ? `Occupé: ${occupe}`
                : ''
              }
              className={cls}
              data-testid={isFerie ? `mini-cal-ferie-${dateStr}` : weekend ? `mini-cal-weekend-${dateStr}` : `mini-cal-day-${dateStr}`}
            >
              {day}
              {isFerie && !sel && <span className="absolute top-0 right-0.5 text-[8px] text-rose-500">F</span>}
              {conge && !sel && !isFerie && <span className="absolute top-0 right-0.5 text-[8px] text-amber-500">C</span>}
              {occupe && !sel && !isFerie && !conge && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-orange-400"></span>}
            </button>
          );
        })}
      </div>
      {selectedDates.length > 0 && <p className="text-xs text-blue-600 mt-2 font-medium">{selectedDates.length} jour{selectedDates.length > 1 ? 's' : ''} sélectionné{selectedDates.length > 1 ? 's' : ''}</p>}
      <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-200"></span> Jour férié CCQ</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-200"></span> Congé employé</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-200"></span> Autre chantier</span>
      </div>
    </div>
  );
}
